import { HOLDER_KINDS, type ComplianceOverviewItem, type ComplianceOverviewListQuery, type HolderKind } from '@chukua/common';
import { Tag, type TableColumnsType } from 'antd';
import { Link } from 'react-router';
import type { ListQueryControls } from '../../../shared/hooks/use-list-query';
import { driverTabPath } from '../../drivers/drivers.paths';
import { trailerTabPath } from '../../fleet/features/trailers/trailers.paths';
import { truckTabPath } from '../../fleet/features/trucks/trucks.paths';
import { OVERVIEW_BUCKET_FILTERS, OVERVIEW_BUCKET_LABELS } from '../policies/overview-bucket-labels';

interface BuildComplianceOverviewColumnsArgs {
  list: Pick<ListQueryControls<ComplianceOverviewListQuery>, 'sortOrderFor' | 'filteredValueFor'>;
  documentTypeOptions: { text: string; value: string }[];
}

const HOLDER_TYPE_LABELS: Record<HolderKind, string> = { truck: 'Truck', trailer: 'Trailer', driver: 'Driver' };
const HOLDER_TYPE_FILTERS = HOLDER_KINDS.map((kind) => ({ text: HOLDER_TYPE_LABELS[kind], value: kind }));

/** Straight to the holder's Documents tab, where the expiring or expired document is. */
function holderPath(item: ComplianceOverviewItem): string {
  if (item.holderType === 'truck') return truckTabPath(item.holderId, 'documents');
  if (item.holderType === 'trailer') return trailerTabPath(item.holderId, 'documents');
  return driverTabPath(item.holderId, 'documents');
}

/** Sorting and filtering run on the server: each column's `key` is its `sort` field or filter param in `complianceOverviewListQuerySchema`. */
export function buildComplianceOverviewColumns({ list, documentTypeOptions }: BuildComplianceOverviewColumnsArgs): TableColumnsType<ComplianceOverviewItem> {
  return [
    {
      title: 'Holder',
      dataIndex: 'holderLabel',
      key: 'holderLabel',
      sorter: true,
      sortOrder: list.sortOrderFor('holderLabel'),
      render: (holderLabel: string, item) => (
        <Link className="font-medium text-link hover:underline" to={holderPath(item)}>
          {holderLabel}
        </Link>
      ),
    },
    {
      title: 'Type',
      key: 'holderType',
      filters: HOLDER_TYPE_FILTERS,
      filteredValue: list.filteredValueFor('holderType'),
      render: (_, item) => HOLDER_TYPE_LABELS[item.holderType],
    },
    {
      title: 'Document',
      key: 'documentTypeId',
      filters: documentTypeOptions,
      filteredValue: list.filteredValueFor('documentTypeId'),
      render: (_, item) => item.documentTypeName,
    },
    {
      title: 'Expiry date',
      dataIndex: 'expiryDate',
      key: 'expiryDate',
      sorter: true,
      sortOrder: list.sortOrderFor('expiryDate'),
      render: (expiryDate: string | null) => (expiryDate ? new Date(expiryDate).toLocaleDateString() : '—'),
    },
    {
      title: 'Status',
      key: 'bucket',
      filters: OVERVIEW_BUCKET_FILTERS,
      filteredValue: list.filteredValueFor('bucket'),
      render: (_, item) => <Tag color={OVERVIEW_BUCKET_LABELS[item.bucket].color}>{OVERVIEW_BUCKET_LABELS[item.bucket].label}</Tag>,
    },
    {
      title: '',
      key: 'actions',
      render: (_, item) => (
        <Link className="text-sm font-medium text-link hover:underline" to={holderPath(item)}>
          View
        </Link>
      ),
    },
  ];
}
